import React from 'react';
import useThemeCanvas, { hexToRgb } from '../useThemeCanvas';

// Soft drifting gradient orbs. Uses a moody backdrop + `screen` blending (not
// `lighter`, which blows colours to white) so orbs read as glowing depth rather
// than flat corporate circles.

export default function BlobsTheme({ palette, colorMode, dark }) {
  const canvasRef = useThemeCanvas({
    init: (ctx, w, h, state) => {
      state.blobs = Array.from({ length: 7 }, (_, i) => {
        const depth = 0.45 + Math.random() * 0.55; // nearer = larger, brighter
        return {
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.25 * depth,
          vy: (Math.random() - 0.5) * 0.25 * depth,
          r: (160 + Math.random() * 180) * depth,
          depth,
          colorIdx: i % Math.max(palette.length, 1),
          phase: Math.random() * Math.PI * 2,
        };
      });
    },
    draw: (ctx, w, h, state, t) => {
      // Backdrop
      const bg = ctx.createLinearGradient(0, 0, 0, h);
      if (dark) {
        bg.addColorStop(0, '#070a18');
        bg.addColorStop(1, '#0d1230');
      } else {
        bg.addColorStop(0, '#eef2fb');
        bg.addColorStop(1, '#e2e8f6');
      }
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      ctx.globalCompositeOperation = dark ? 'screen' : 'source-over';

      for (const b of state.blobs) {
        b.x += b.vx;
        b.y += b.vy;
        if (b.x < -b.r) b.x = w + b.r;
        if (b.x > w + b.r) b.x = -b.r;
        if (b.y < -b.r) b.y = h + b.r;
        if (b.y > h + b.r) b.y = -b.r;

        const breath = 0.9 + 0.12 * Math.sin(t * 0.25 + b.phase);
        const R = b.r * breath;
        const cx = b.x + Math.sin(t * 0.18 + b.phase) * 24;
        const cy = b.y + Math.cos(t * 0.14 + b.phase) * 18;

        const idx = (b.colorIdx + Math.floor(t * (colorMode === 'cycling' ? 0.3 : 0.02))) % palette.length;
        const rgb = hexToRgb(palette[idx]);
        const rgb2 = hexToRgb(palette[(idx + 1) % palette.length]);
        const core = (dark ? 0.5 : 0.42) * b.depth;

        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, R);
        grad.addColorStop(0, `rgba(${rgb.r},${rgb.g},${rgb.b},${core})`);
        grad.addColorStop(0.35, `rgba(${rgb.r},${rgb.g},${rgb.b},${core * 0.55})`);
        grad.addColorStop(0.7, `rgba(${rgb2.r},${rgb2.g},${rgb2.b},${core * 0.18})`);
        grad.addColorStop(1, `rgba(${rgb2.r},${rgb2.g},${rgb2.b},0)`);

        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(cx, cy, R, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalCompositeOperation = 'source-over';

      // Subtle vignette for depth (dark mode only).
      if (dark) {
        const vig = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.4, w / 2, h / 2, Math.max(w, h) * 0.75);
        vig.addColorStop(0, 'rgba(0,0,0,0)');
        vig.addColorStop(1, 'rgba(0,0,0,0.45)');
        ctx.fillStyle = vig;
        ctx.fillRect(0, 0, w, h);
      }
    },
  });
  return <canvas ref={canvasRef} className="absolute inset-0" />;
}
